import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Drama } from '@/types';

interface HeroCarouselProps {
  items: Drama[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(timer);
  }, [items.length]);

  const go = (dir: number) => setIndex((i) => (i + dir + items.length) % items.length);

  return (
    <section className="relative w-full h-[520px] sm:h-[560px] lg:h-[660px] overflow-hidden bg-secondary-900">
      {items.map((drama, i) => (
        <div
          key={drama.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          <img
            src={drama.backdrop}
            alt={`${drama.title} 剧照`}
            title={`${drama.title} - 韩剧电视剧`}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/10"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full max-w-[1280px] mx-auto px-4 md:px-8 flex flex-col justify-center">
        <div key={items[index].id} className="max-w-2xl animate-fade-up">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1 bg-primary-500 text-white text-xs font-medium px-3 py-1 rounded-full">
              <i className="ri-fire-line"></i> 正在热播
            </span>
            {items[index].genres.map((g) => (
              <span key={g} className="text-xs text-white/85 border border-white/30 rounded-full px-2.5 py-1">
                {g}
              </span>
            ))}
          </div>
          <h2 className="font-heading font-bold text-white text-3xl md:text-5xl leading-tight">
            {items[index].title}
          </h2>
          <p className="mt-2 text-sm text-white/70 tracking-wide">{items[index].titleEn}</p>
          <p className="mt-4 text-sm md:text-base text-white/85 leading-relaxed line-clamp-3">
            {items[index].synopsis}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/80">
            <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {items[index].year}</span>
            <span className="flex items-center gap-1"><i className="ri-stack-line"></i> 全 {items[index].episodes} 集</span>
            <span className="flex items-center gap-1"><i className="ri-star-fill text-accent-400"></i> {items[index].rating.toFixed(1)}</span>
            <span className="flex items-center gap-1"><i className="ri-user-star-line"></i> 导演 {items[index].director}</span>
          </div>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${items[index].id}`}
              className="cursor-pointer whitespace-nowrap inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-6 py-3 rounded-full transition-colors"
            >
              <i className="ri-play-circle-line text-lg"></i> 查看剧集详情
            </Link>
            <Link
              to="/category/romance"
              className="cursor-pointer whitespace-nowrap inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white text-sm font-medium px-6 py-3 rounded-full backdrop-blur-sm transition-colors"
            >
              <i className="ri-compass-3-line text-lg"></i> 浏览更多题材
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute z-10 bottom-6 md:bottom-8 right-4 md:right-8 flex items-center gap-3">
        <button
          onClick={() => go(-1)}
          aria-label="上一张"
          className="cursor-pointer w-10 h-10 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 border border-white/30 text-white backdrop-blur-sm transition-colors"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          onClick={() => go(1)}
          aria-label="下一张"
          className="cursor-pointer w-10 h-10 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 border border-white/30 text-white backdrop-blur-sm transition-colors"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>

      <div className="absolute z-10 bottom-7 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {items.map((d, i) => (
          <button
            key={d.id}
            onClick={() => setIndex(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${i === index ? 'w-8 bg-primary-500' : 'w-3 bg-white/50 hover:bg-white/80'}`}
          ></button>
        ))}
      </div>
    </section>
  );
}